/**
 * Accueil, SOUS la ligne de flottaison.
 *
 * Le premier ecran ne bouge pas d'un pixel : titre, pseudo, trois boutons, fond
 * anime, plein viewport. Tout ce qui suit n'existe que pour qui fait defiler,
 * et pour les moteurs, qui ne voyaient jusqu'ici qu'une page de quatre boutons
 * sans un mot sur le jeu.
 */

export interface Step {
  readonly title: string;
  readonly text: string;
  /** Illustration : un tableau du catalogue, deja servi par le jeu. */
  readonly painting: string;
  /** Etat de la case dans la vignette : c'est lui qui raconte l'etape. */
  readonly stage: 'masked' | 'painted' | 'hunt' | 'reveal';
}

export const HOME_STEPS: readonly Step[] = [
  {
    title: 'Un tableau troué',
    text: 'Vous recevez une illustration dont une case a été effacée.',
    painting: '1_airIsland',
    stage: 'masked',
  },
  {
    title: 'Vous repeignez',
    text: 'Vous redessinez la case pour qu’elle se fonde dans l’œuvre.',
    painting: '1_airIsland',
    stage: 'painted',
  },
  {
    title: 'La traque',
    text: 'Le tableau revient avec les retouches de tous : trouvez-les avant les autres.',
    painting: '1_airIsland',
    stage: 'hunt',
  },
  {
    title: 'La révélation',
    text: 'Chaque cachette est dévoilée, et les points tombent.',
    painting: '1_airIsland',
    stage: 'reveal',
  },
];

export interface ModeCard {
  readonly name: string;
  readonly text: string;
}

export const HOME_MODES: readonly ModeCard[] = [
  {
    name: 'Chasse commune',
    text: 'Tout le monde peint sur le même tableau, chacun sa case. Puis la chasse s’ouvre pour tous en même temps : une retouche ne se trouve qu’une fois, par le premier à la repérer. C’est le mode par défaut, celui où il se passe toujours quelque chose.',
  },
  {
    name: 'Diffusion',
    text: 'L’hôte ne dessine pas : il cherche, seul, pendant que tous les autres se cachent dans son tableau. Pensé pour le direct, où la chasse devient le spectacle. Gratuit pour toujours.',
  },
  {
    name: 'Chacun son tableau',
    text: 'Une œuvre par joueur. Vous ne cherchez la retouche que d’une seule personne, en un seul clic. La règle la plus simple du jeu, et la plus tendue.',
  },
  {
    name: 'Un seul dessine',
    text: 'Une manche, un dessinateur, tous les autres à sa poursuite. Le rôle tourne, et le dessinateur regarde sa propre cachette tomber ou tenir.',
  },
  {
    name: 'Battle royale',
    text: 'La même chasse, mais la première retouche découverte élimine son auteur. On joue jusqu’au dernier debout, sans compter un seul point.',
  },
  {
    name: 'Concours',
    text: 'Personne ne cherche. Tout le monde repeint la même case du même tableau, puis chacun vote pour la retouche qu’il trouve la plus réussie. Une voix reçue vaut un point.',
  },
];

export interface FaqEntry {
  readonly question: string;
  readonly answer: string;
}

export const HOME_FAQ: readonly FaqEntry[] = [
  {
    question: 'Combien faut-il de joueurs ?',
    answer:
      'De 2 à 10. Si vous êtes seul, un adversaire automatique prend le siège libre pour que la partie démarre tout de suite. À partir de quatre, la chasse commune prend toute son ampleur.',
  },
  {
    question: 'Est-ce que c’est gratuit ?',
    answer:
      'Oui. La partie rapide, la chasse commune et le mode diffusion sont gratuits, sans limite de temps ni de nombre de parties. Les autres modes le sont également pour l’instant.',
  },
  {
    question: 'Faut-il créer un compte ?',
    answer:
      'Non. On tape un pseudo, on clique, on joue. Un compte sert uniquement à conserver son pseudo et son avatar d’une partie à l’autre : il reste facultatif.',
  },
  {
    question: 'Est-ce que ça marche sur téléphone ?',
    answer:
      'Oui, l’interface s’adapte au tactile : barre d’outils repliée, loupe sous le doigt, double appui pour zoomer. Cela dit, on dessine plus finement à la souris et sur un grand écran.',
  },
  {
    question: 'Comment j’invite mes amis ?',
    answer:
      'Créez un salon, copiez le lien d’invitation et collez-le où vous voulez. Vos amis arrivent directement à table, sans compte ni installation.',
  },
  {
    question: 'C’est quoi une « retouche » ?',
    answer:
      'C’est ce que vous peignez dans la case effacée. Réussie, elle passe pour un morceau du tableau d’origine et personne ne la trouve. Ratée, elle saute aux yeux.',
  },
];

export const HOME_INTRO: readonly string[] = [
  'Retusch est un jeu de dessin en ligne pour 2 à 10 joueurs, jouable dans le navigateur, sans installation ni compte. Chaque manche vous confie un tableau dont une case a été effacée : à vous de la repeindre pour qu’elle disparaisse dans l’œuvre. Puis les rôles s’inversent, et il faut débusquer les retouches des autres.',
  'La plupart des jeux de dessin récompensent celui qu’on reconnaît le plus vite. Retusch demande l’inverse : réussir, c’est ne pas être vu.',
];
